<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon left>mdi-account-group</v-icon>
      出勤紀錄 ({{ date }})
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <v-select
          dense
          hide-details
          class="ma-0"
          style="max-width: 120px"
          :items="statusOptions"
          v-model="statusFilter"
          label="狀態"
          item-text="text"
          item-value="value"
          @change="onStatusFilterChange"
        ></v-select>
      </div>
    </v-card-title>
    <v-card-text class="py-0">
      <!-- List View -->
      <v-simple-table v-if="viewMode === 'list'">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">姓名</th>
              <th class="text-center">上班</th>
              <th class="text-center">下班</th>
              <th class="text-center">總時數</th>
              <th class="text-center">加班</th>
              <th class="text-center">請假</th>
              <th class="text-center">狀態</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="record in formattedRecords"
              :key="record.id"
              :title="record.leaveHint"
            >
              <td>
                <div>
                  <span
                    class="text-sm font-weight-bold text-body hover-scale"
                    style="cursor: pointer; text-decoration: underline"
                    @click="goToMonthlyClockRecords(record.user_id)"
                    title="查看每月打卡紀錄"
                  >
                    {{ record.name }}
                  </span>
                </div>
                <!-- <div class="text-caption grey--text">{{ record.date }}</div> -->
              </td>
              <td
                class="text-center"
                :title="formatDateTime(record.clock_in_actual)"
              >
                {{ record.clockIn }}
              </td>
              <td
                class="text-center"
                :title="formatDateTime(record.clock_out_actual)"
              >
                {{ record.clockOut }}
              </td>
              <td class="text-center">{{ record.totalHours }}</td>
              <!-- TODO 後續可以用tip 顯示加班單 -->
              <td class="text-center">
                {{ getIsOvertime(record.overtime) }}
              </td>
              <td class="text-center">
                <div
                  v-for="leave in record.leaveRecordsFormatted"
                  :key="leave.id"
                  class="text-xs"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                        style="cursor: pointer; border-bottom: 1px dashed #ccc"
                        class="d-inline-block text-no-wrap"
                      >
                        {{ leave.typeText }} {{ leave.leave_hours }}小時
                      </span>
                    </template>
                    <span>
                      {{ leave.start_time }} ~
                      {{ leave.end_time }}
                    </span>
                  </v-tooltip>
                </div>
              </td>
              <td class="text-center">
                <v-chip
                  v-for="(status, index) in ensureArray(record.status)"
                  :key="index"
                  small
                  :color="getStatusColor(status)"
                  text-color="white"
                  class="mr-1 mb-1"
                >
                  {{ getStatusText(status) }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Grid/Card View for Mobile -->
      <div v-if="viewMode === 'grid'" class="py-4">
        <v-row>
          <v-col
            v-for="record in formattedRecords"
            :key="record.id"
            cols="12"
            sm="6"
            class="mb-2"
          >
            <v-card
              outlined
              class="border-radius-lg card-shadow"
              :title="record.leaveHint"
            >
              <v-card-text class="pa-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="font-weight-bold text-typo">
                    <span
                      class="hover-scale"
                      style="cursor: pointer; text-decoration: underline"
                      @click="goToMonthlyClockRecords(record.user_id)"
                      title="查看每月打卡紀錄"
                    >
                      {{ record.name }}
                    </span>
                  </div>
                  <div class="d-flex flex-wrap justify-end">
                    <v-chip
                      v-for="(status, index) in ensureArray(record.status)"
                      :key="index"
                      x-small
                      :color="getStatusColor(status)"
                      text-color="white"
                      class="ml-1 mb-1"
                    >
                      {{ getStatusText(status) }}
                    </v-chip>
                  </div>
                </div>
                <v-row no-gutters class="text-body-2">
                  <v-col cols="6">
                    <div class="text-caption grey--text">上班</div>
                    <div>{{ record.clockIn || "-" }}</div>
                  </v-col>
                  <v-col cols="6">
                    <div class="text-caption grey--text">下班</div>
                    <div>{{ record.clockOut || "-" }}</div>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption grey--text">總時數</div>
                    <div>{{ record.totalHours || "0" }}</div>
                  </v-col>
                  <v-col cols="4" class="mt-2">
                    <div class="text-caption grey--text">加班</div>
                    <div>{{ getIsOvertime(record.overtime) }}</div>
                  </v-col>
                  <v-col
                    cols="4"
                    class="mt-2"
                    v-if="record.leaveRecordsFormatted.length"
                  >
                    <div class="text-caption grey--text">請假</div>
                    <div
                      v-for="leave in record.leaveRecordsFormatted"
                      :key="leave.id"
                      class="text-no-wrap"
                    >
                      {{ leave.typeText }} {{ leave.leave_hours }}小時
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-if="formattedRecords.length === 0"
          class="text-center py-4 grey--text"
        >
          目前無出勤紀錄
        </div>
      </div>
    </v-card-text>
    <v-card-actions>
      <v-row class="w-100">
        <v-col cols="6" class="ml-auto d-flex justify-end py-0">
          <v-pagination
            prev-icon="fa fa-angle-left"
            next-icon="fa fa-angle-right"
            class="pagination"
            color="#cb0c9f"
            v-model="page"
            :length="pageCount"
            @input="onPageChange"
            circle
          ></v-pagination>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { LEAVE_TYPE } from "@/constants";

import {
  AttendanceStatus,
  StatusDisplayText,
  calculateWorkingHours,
  getStatusText,
  getStatusColor as getStatusColorUtil,
} from "@/utils/attendance";
import {
  formatTimeOnly as formatTimeOnlyUtil,
  formatDateTime as formatDateTimeUtil,
} from "@/utils/time";

export default {
  name: "AttendanceRecords",
  props: {
    date: {
      type: String,
      default: () => {
        const today = new Date();
        return today.toISOString().split("T")[0]; // Format: YYYY-MM-DD
      },
    },
    pageSize: {
      type: Number,
      default: 10,
    },
  },

  data() {
    return {
      records: [],
      loading: false,
      error: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: this.pageSize,
      pagination: {
        currentPage: 1,
        pageSize: this.pageSize,
        totalItems: 0,
        totalPages: 1,
      },
      statusFilter: null,
      viewMode: "list",
    };
  },

  computed: {
    ...mapGetters("company", ["companies", "selectedCompany"]),
    statusOptions() {
      return [
        {
          text: StatusDisplayText[AttendanceStatus.LATE],
          value: AttendanceStatus.LATE,
        },
        {
          text: StatusDisplayText[AttendanceStatus.EARLY_LEAVE],
          value: AttendanceStatus.EARLY_LEAVE,
        },
        {
          text: StatusDisplayText[AttendanceStatus.PRESENT],
          value: AttendanceStatus.PRESENT,
        },
        {
          text: StatusDisplayText[AttendanceStatus.ABSENT],
          value: AttendanceStatus.ABSENT,
        },
        {
          text: StatusDisplayText[AttendanceStatus.LEAVE],
          value: AttendanceStatus.LEAVE,
        },
        {
          text: StatusDisplayText[AttendanceStatus.OVERTIME],
          value: AttendanceStatus.OVERTIME,
        },
        {
          text: StatusDisplayText[AttendanceStatus.ABNORMAL],
          value: AttendanceStatus.ABNORMAL,
        },
      ];
    },
    formattedRecords() {
      const formattedData = this.records.map((record) => {
        // Format clock-in time
        const clock_in_actual_display =
          formatTimeOnlyUtil(record.clock_in_actual) || "";

        // Format clock-out time
        const clock_out_actual_display =
          formatTimeOnlyUtil(record.clock_out_actual) || "";

        // Calculate total hours if both clock-in and clock-out exist
        // let totalHours = "";
        // if (record.clock_in_actual && record.clock_out_actual) {
        //   const clock_in_actual = new Date(record.clock_in_actual);
        //   const clockOut = new Date(record.clock_out_actual);
        //   const diffMs = clockOut - clock_in_actual;
        //   const diffHrs = diffMs / (1000 * 60 * 60);
        //   totalHours = diffHrs.toFixed(2);
        // }
        const totalHours = calculateWorkingHours(record);

        // Determine status using the value provided by the backend
        const status = record.status || [];

        // Calculate leave hint if status includes "LEAVE"
        let leaveHint = "";
        if (
          status.includes(AttendanceStatus.LEAVE) &&
          record.leave_records &&
          record.leave_records.length > 0
        ) {
          leaveHint = record.leave_records
            .map((lr) => {
              const typeText = LEAVE_TYPE[lr.leave_type] || "未知假別";
              const start = formatDateTimeUtil(lr.start_time);
              const end = formatDateTimeUtil(lr.end_time);
              return `${typeText}: ${start} - ${end}`;
            })
            .join("\n");
        }

        return {
          id: record.id,
          name: record.user?.name || "",
          user_id: record.user_id || (record.user ? record.user.id : null),
          date: record.clock_date || "",
          clockIn: clock_in_actual_display, // display
          clockOut: clock_out_actual_display, //display
          clock_in_actual: record.clock_in_actual,
          clock_out_actual: record.clock_out_actual,
          clock_in_time: record.clock_in_time,
          clock_out_time: record.clock_out_time,
          totalHours: totalHours,
          status: status, // Provided by backend
          leaveRecordsFormatted:
            status.includes(AttendanceStatus.LEAVE) && record.leave_records
              ? record.leave_records.map((lr) => ({
                  id: lr.id,
                  typeText: LEAVE_TYPE[lr.leave_type] || "未知假別",
                  leave_hours: parseFloat((lr.leave_minutes / 60).toFixed(1)),
                  start_time: formatDateTimeUtil(lr.start_time),
                  end_time: formatDateTimeUtil(lr.end_time),
                }))
              : [],
          overtime: record.overtime,
          leaveHint: leaveHint,
        };
      });

      return formattedData;
    },
  },

  created() {
    if (this.$vuetify.breakpoint.mobile) {
      this.viewMode = "grid";
    }
    this.fetchAttendanceRecords();
  },

  watch: {
    date() {
      this.page = 1;
      this.fetchAttendanceRecords();
    },
    selectedCompany() {
      this.page = 1;
      this.fetchAttendanceRecords();
    },
  },

  methods: {
    async fetchAttendanceRecords() {
      this.loading = true;
      this.error = null;
      //  if selectedCompany is null, return
      if (!this.selectedCompany) {
        this.loading = false;
        return;
      }
      const qp = {
        page: this.page,
        limit: this.itemsPerPage,
        start_date: this.date,
        end_date: this.date,
        company_id: this.selectedCompany.id,
      };
      if (
        this.statusFilter !== null &&
        this.statusFilter !== undefined &&
        this.statusFilter !== ""
      ) {
        qp.status = this.statusFilter;
      }
      const queryParams = new URLSearchParams(qp);
      const response = await this.$axios.get(
        `/api/clock-records?${queryParams}`
      );
      this.records = response.data.rows || [];
      this.pagination = response.data.pagination;
      if (this.pagination) {
        this.pageCount = this.pagination.totalPages || 0;
      }
      this.loading = false;
      //update stats
      // Emit the stats to parent component
      const stats = response.data.stats;
      this.$emit("update-stats", stats);
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.fetchAttendanceRecords();
    },
    onStatusFilterChange() {
      this.page = 1;
      this.fetchAttendanceRecords();
    },

    goToMonthlyClockRecords(userId) {
      if (!userId) return;
      this.$emit("view-monthly", userId);
      this.$router
        .push({
          path: "/pages/dashboards/clock-records",
          query: {
            tab: "1",
            user_id: userId,
          },
        })
        .catch(() => {});
    },
    getStatusText(status) {
      return getStatusText(status);
    },
    ensureArray(status) {
      if (Array.isArray(status)) return status;
      if (status === null || status === undefined) return [];
      return [status];
    },
    getStatusColor(status) {
      return getStatusColorUtil(status);
    },
    getIsOvertime(overtime) {
      if (overtime == false) {
        return "否";
      } else if (overtime == true) {
        return "是";
      }
      return "";
    },
    formatDateTime(date) {
      return formatDateTimeUtil(date);
    },
  },
};
</script>
